import { useRef, type KeyboardEvent } from 'react'
import { cn } from '@/utils/cn'

export type UnderlineTab<T extends string> = {
  id: T
  label: string
  /** Rendered as a pill beside the label. Omit for tabs that have nothing to count. */
  count?: number
}

/**
 * The page's tab control, and the only one on this surface — the section tabs and the slots
 * calendar's Virtual/On-site switch are the same component so they cannot drift apart.
 *
 * Coral underline over a `#ECEEF1` hairline, matching the Messages thread tabs. Implements the ARIA
 * tabs pattern properly: the tablist is a single tab stop and Left/Right/Home/End move between
 * tabs, so a keyboard user is not forced to tab through every panel to reach the next section.
 */
export function UnderlineTabs<T extends string>({
  tabs,
  value,
  onChange,
  idPrefix,
  label,
  panelId,
  className,
}: {
  tabs: readonly UnderlineTab<T>[]
  value: T
  onChange: (next: T) => void
  /** Namespaces the generated `id`s, so two tablists on one screen stay distinct. */
  idPrefix: string
  /** Accessible name for the tablist. */
  label: string
  panelId: string
  className?: string
}) {
  const refs = useRef<Partial<Record<T, HTMLButtonElement | null>>>({})

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const current = tabs.findIndex((tab) => tab.id === value)
    let next = current
    if (event.key === 'ArrowRight') next = (current + 1) % tabs.length
    else if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = tabs.length - 1
    else return

    event.preventDefault()
    onChange(tabs[next].id)
    refs.current[tabs[next].id]?.focus()
  }

  return (
    <div
      role="tablist"
      aria-label={label}
      onKeyDown={handleKeyDown}
      className={cn(['flex gap-1 border-b border-[#ECEEF1]', className])}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === value
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`${idPrefix}-${tab.id}`}
            ref={(node) => {
              refs.current[tab.id] = node
            }}
            aria-selected={isActive}
            aria-controls={panelId}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab.id)}
            className={cn([
              'behuman-v2-mulish relative flex items-center gap-2 px-4 py-3 !text-[15px] transition-colors outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#E5533D]',
              isActive
                ? '!font-bold text-[#E5533D]'
                : '!font-semibold text-[#6B7280] hover:text-[#1F2733]',
            ])}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span
                aria-hidden
                className={cn([
                  'inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-bold',
                  isActive ? 'bg-[#FCF1EE] text-[#E5533D]' : 'bg-[#F1F2F4] text-[#6B7280]',
                ])}
              >
                {tab.count}
              </span>
            )}
            {isActive && (
              <span
                aria-hidden
                className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-[#E5533D]"
              />
            )}
          </button>
        )
      })}
    </div>
  )
}
